import React from "react";
import { useI18n, DOJOOS_ROLES, getModulesForRole, getModulePhaseLabel } from "@/coquille-bridge";
import { useDataStore } from "@/components/admin/useDataStore.js";
import { KARATE_MAIN_ORGANIZATION } from "@/data/dojoOsOrgModel.js";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";

const ROLE_STATUS = {
  active: { fr: "Phase 1 — actif", en: "Phase 1 — active" },
  coming_soon: { fr: "Phase 2 — à venir", en: "Phase 2 — coming soon" },
  future: { fr: "Futur", en: "Future" },
};

export function KarateAccountsPanel({ accounts = [], testId = "karate-accounts-panel" }) {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const { localizeDemo, statusLabel } = useKarateUi();
  const store = useDataStore();

  const dojoLabel = (dojoId) => {
    if (!dojoId) return "—";
    const nom = store.dojos.find((d) => d.id === dojoId)?.nom ?? dojoId;
    return localizeDemo(nom);
  };

  return (
    <div className="gsd-admin-panel karate-accounts-panel" data-testid={testId}>
      <p className="muted" data-testid="accounts-demo-notice">
        {locale === "en"
          ? "Local demo accounts — no real backend security."
          : "Comptes démo locaux — aucune sécurité backend réelle."}
      </p>
      <table className="gsd-admin-panel__table data-table">
        <thead>
          <tr>
            <th>{locale === "en" ? "Username" : "Identifiant"}</th>
            <th>{locale === "en" ? "Display name" : "Nom"}</th>
            <th>{locale === "en" ? "Primary role" : "Rôle principal"}</th>
            <th>{locale === "en" ? "Accessible roles" : "Rôles accessibles"}</th>
            <th>Dojo</th>
            <th>{locale === "en" ? "Status" : "Statut"}</th>
            <th>{locale === "en" ? "Actions" : "Actions"}</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr key={a.id} data-testid={`karate-account-row-${a.username}`}>
              <td>{a.username}</td>
              <td>
                {locale === "en" ? a.nameEn || a.name : a.name} <DemoBadge />
              </td>
              <td>{a.role}</td>
              <td>{(a.accessibleRoles || []).join(", ")}</td>
              <td>{dojoLabel(a.dojoId)}</td>
              <td>{statusLabel(a.statut)}</td>
              <td>
                <span className="btn-row btn-row--compact">
                  <button type="button" className="btn-link" disabled title="Démo">
                    {locale === "en" ? "View" : "Voir"}
                  </button>
                  <button type="button" className="btn-link" disabled>
                    {locale === "en" ? "Edit" : "Modifier"}
                  </button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function KarateRolesPanel({ testId = "karate-roles-panel" }) {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const roles = [...DOJOOS_ROLES.filter((r) => r.id !== "guest"), { id: "guest", labelFr: "Invité", labelEn: "Guest", icon: "🌐", descriptionFr: "Visiteur public.", descriptionEn: "Public visitor.", availability: "active", defaultModules: ["help"] }];

  return (
    <div className="gsd-admin-panel karate-roles-panel" data-testid={testId}>
      <ul className="karate-roles-list">
        {roles.map((r) => {
          const mods = getModulesForRole(r.id === "guest" ? "guest" : r.id === "manager" ? "manager" : r.id === "player" ? "player" : r.id);
          const status = ROLE_STATUS[r.availability] || ROLE_STATUS.active;
          return (
            <li key={r.id} className="karate-roles-list__item" data-testid={`karate-role-card-${r.id}`}>
              <h3>
                <span aria-hidden="true">{r.icon}</span> {locale === "en" ? r.labelEn : r.labelFr}
                <span className="karate-roles-list__status">{locale === "en" ? status.en : status.fr}</span>
              </h3>
              <p>{locale === "en" ? r.descriptionEn : r.descriptionFr}</p>
              <p className="muted">
                {locale === "en" ? "Modules" : "Modules"} : {mods.slice(0, 8).map((m) => (locale === "en" ? m.labelEn : m.labelFr)).join(" · ")}
                {mods.length > 8 ? "…" : ""}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function KarateAccessRequestsPanel({ requests = [], onUpdateStatus, testId = "karate-access-requests-panel" }) {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";

  const statusLabel = (s) => {
    const map = {
      a_valider: locale === "en" ? "To validate" : "À valider",
      approuvee: locale === "en" ? "Approved" : "Approuvée",
      refusee: locale === "en" ? "Rejected" : "Refusée",
      precision: locale === "en" ? "More info needed" : "Précision demandée",
      reçue: locale === "en" ? "Received" : "Reçue",
    };
    return map[s] || s;
  };

  return (
    <div className="gsd-admin-panel karate-requests-panel" data-testid={testId}>
      {requests.length === 0 ? (
        <p className="muted">{locale === "en" ? "No requests [DEMO]." : "Aucune demande [DÉMO]."}</p>
      ) : (
        <table className="gsd-admin-panel__table data-table">
          <thead>
            <tr>
              <th>{locale === "en" ? "Name" : "Nom"}</th>
              <th>{locale === "en" ? "Email" : "Courriel"}</th>
              <th>{locale === "en" ? "Role" : "Rôle"}</th>
              <th>Dojo</th>
              <th>{locale === "en" ? "Status" : "Statut"}</th>
              <th>{locale === "en" ? "Actions" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} data-testid={`karate-access-request-${r.id}`}>
                <td>{r.name}</td>
                <td>{r.email}</td>
                <td>{r.role}</td>
                <td>{r.organization || "—"}</td>
                <td>{statusLabel(r.status)}</td>
                <td>
                  <span className="btn-row btn-row--compact">
                    <button type="button" className="btn-link" onClick={() => onUpdateStatus?.(r.id, "approuvee")} data-testid={`req-approve-${r.id}`}>
                      {locale === "en" ? "Approve" : "Approuver"}
                    </button>
                    <button type="button" className="btn-link" onClick={() => onUpdateStatus?.(r.id, "refusee")} data-testid={`req-reject-${r.id}`}>
                      {locale === "en" ? "Reject" : "Refuser"}
                    </button>
                    <button type="button" className="btn-link" onClick={() => onUpdateStatus?.(r.id, "precision")} data-testid={`req-info-${r.id}`}>
                      {locale === "en" ? "Ask info" : "Précision"}
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function KarateOrganisationBanner({ testId = "karate-org-banner" }) {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const org = KARATE_MAIN_ORGANIZATION;
  return (
    <div className="karate-org-banner" data-testid={testId}>
      <h2>{org.nom}</h2>
      <p>
        {locale === "en" ? "Type" : "Type"} : {org.type} · {locale === "en" ? "Status" : "Statut"} : {org.statut} <DemoBadge />
      </p>
      <p>{locale === "en" ? org.messageEn : org.messageFr}</p>
      <p className="muted">
        {locale === "en"
          ? `Target: ${org.objectifDojos} dojos — ${org.dojosDemo} demo placeholders, 0 official confirmed.`
          : `Objectif : ${org.objectifDojos} dojos — ${org.dojosDemo} emplacements [DÉMO], 0 officiel confirmé.`}
      </p>
    </div>
  );
}

export function KarateDojosOrgSummary({ dojos = [], instructeurs = [], etudiants = [], testId = "karate-dojos-org-summary" }) {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const { localizeDemo, statusLabel } = useKarateUi();
  return (
    <div className="karate-dojos-summary" data-testid={testId}>
      <KarateOrganisationBanner />
      <table className="gsd-admin-panel__table data-table">
        <thead>
          <tr>
            <th>Dojo</th>
            <th>{locale === "en" ? "City" : "Ville"}</th>
            <th>{locale === "en" ? "Status" : "Statut"}</th>
            <th>Sensei</th>
            <th>Sempai</th>
            <th>{locale === "en" ? "Demo students" : "Étudiants démo"}</th>
          </tr>
        </thead>
        <tbody>
          {dojos.map((d) => (
            <tr key={d.id} data-testid={`karate-dojo-row-${d.id}`}>
              <td>{localizeDemo(d.nom)}</td>
              <td>{localizeDemo(d.ville)}</td>
              <td>{statusLabel(d.statut)}</td>
              <td>{localizeDemo(instructeurs.find((i) => i.id === d.sensei_id)?.nom ?? "—")}</td>
              <td>{localizeDemo(instructeurs.find((i) => i.id === d.sempai_id)?.nom ?? "—")}</td>
              <td>{etudiants.filter((e) => e.dojo_id === d.id).length}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted">{locale === "en" ? "Awaiting official 10-dojo file from client." : "En attente du fichier officiel des 10 dojos client."}</p>
    </div>
  );
}
